import { useEffect, useState } from "react";
import {
  DndContext,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ─── Droppable контейнер ───────────────────────────────────────────
function DroppableContainer({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        minHeight: 120,
        border: isOver ? "2px dashed #4caf50" : "2px dashed #ccc",
        borderRadius: 8,
        padding: 8,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        transition: "border 0.2s",
      }}
    >
      {children}
    </div>
  );
}

// ─── Одиночный элемент ────────────────────────────────────────────
function SortableItem({ file, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: file.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div {...listeners} style={{ cursor: "grab" }}>
        <img
          src={file.preview}
          alt="preview"
          width={100}
          height={100}
          style={{ objectFit: "cover", borderRadius: "6px" }}
        />
      </div>

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          background: "rgba(0,0,0,0.6)",
          color: "white",
          padding: "2px 4px",
          fontSize: "12px",
        }}
      >
        {index + 1}
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(file.id);
        }}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          background: "red",
          color: "white",
          border: "none",
          borderRadius: "50%",
          width: 24,
          height: 24,
          cursor: "pointer",
          zIndex: 10,
        }}
      >
        ×
      </button>
    </div>
  );
}

// ─── Главный компонент ────────────────────────────────────────────
export default function FileAndDocumentUploader({
  editData,
  files,
  setFiles,
  handleFileChange,
  removeFile,
  documentFiles,
  setDocumentFiles,
  handleDocumentFileChange,
  removeDocumentFile,
}) {
  const sensors = useSensors(useSensor(PointerSensor));
  const [activeFile, setActiveFile] = useState(null);

  // Загружаем editData → files
  useEffect(() => {
    if (editData?.urls?.length) {
      setFiles(
        editData.urls.map((url, idx) => ({
          id: `existing-${idx}`,
          preview: url,
          isExisting: true,
        })),
      );
    }
  }, [editData, setFiles]);

  // Загружаем editData → documentFiles
  useEffect(() => {
    if (editData?.docurls?.length) {
      setDocumentFiles(
        editData.docurls.map((url, idx) => ({
          id: `existing-doc-${idx}`,
          preview: url,
          isExisting: true,
        })),
      );
    }
  }, [editData, setDocumentFiles]);

  // Определяем контейнер по id элемента или контейнера
  const findContainer = (id) => {
    if (id === "files-drop" || files.find((f) => f.id === id)) return "files";
    if (id === "docs-drop" || documentFiles.find((f) => f.id === id))
      return "docs";
    return null;
  };

  const handleDragStart = ({ active }) => {
    const allFiles = [...files, ...documentFiles];
    setActiveFile(allFiles.find((f) => f.id === active.id) || null);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveFile(null);
    if (!over) return;

    const fromContainer = findContainer(active.id);
    const toContainer = findContainer(over.id);

    if (!fromContainer || !toContainer) return;

    // ── Сортировка внутри одного списка ──
    if (fromContainer === toContainer) {
      // если дропнули на контейнер а не на элемент — ничего не делаем
      if (active.id === over.id) return;
      if (over.id === "files-drop" || over.id === "docs-drop") return;

      if (fromContainer === "files") {
        const oldIdx = files.findIndex((f) => f.id === active.id);
        const newIdx = files.findIndex((f) => f.id === over.id);
        setFiles(arrayMove(files, oldIdx, newIdx));
      } else {
        const oldIdx = documentFiles.findIndex((f) => f.id === active.id);
        const newIdx = documentFiles.findIndex((f) => f.id === over.id);
        setDocumentFiles(arrayMove(documentFiles, oldIdx, newIdx));
      }
      return;
    }

    // ── Перемещение из files → docs ──
    if (fromContainer === "files" && toContainer === "docs") {
      const item = files.find((f) => f.id === active.id);
      if (!item) return;

      setFiles((prev) => prev.filter((f) => f.id !== active.id));

      setDocumentFiles((prev) => {
        const overIdx = prev.findIndex((f) => f.id === over.id);
        const next = [...prev];
        next.splice(overIdx >= 0 ? overIdx : next.length, 0, item);
        return next;
      });
    }

    // ── Перемещение из docs → files ──
    if (fromContainer === "docs" && toContainer === "files") {
      const item = documentFiles.find((f) => f.id === active.id);
      if (!item) return;

      setDocumentFiles((prev) => prev.filter((f) => f.id !== active.id));

      setFiles((prev) => {
        const overIdx = prev.findIndex((f) => f.id === over.id);
        const next = [...prev];
        next.splice(overIdx >= 0 ? overIdx : next.length, 0, item);
        return next;
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* ── Секция фото ── */}
        <div className="uploadingImg">
          <h4>📂 ԳՈՒՅՔԻ ՆԿԱՐՆԵՐԸ</h4>
          <input type="file" multiple onChange={handleFileChange} />
          <SortableContext
            items={files.map((f) => f.id)}
            strategy={horizontalListSortingStrategy}
          >
            <DroppableContainer id="files-drop">
              {files.map((file, index) => (
                <SortableItem
                  key={file.id}
                  file={file}
                  index={index}
                  onRemove={removeFile}
                />
              ))}
              {files.length === 0 && (
                <span style={{ color: "#aaa", alignSelf: "center" }}>
                  Загрузи фото
                </span>
              )}
            </DroppableContainer>
          </SortableContext>
        </div>

        {/* ── Секция документов ── */}
        <div className="uploadingImg">
          <h4>📂 ՓԱՍՏԱԹՂԹԵՐԻ ՆԿԱՐՆԵՐԸ</h4>
          <input type="file" multiple onChange={handleDocumentFileChange} />
          <SortableContext
            items={documentFiles.map((f) => f.id)}
            strategy={horizontalListSortingStrategy}
          >
            <DroppableContainer id="docs-drop">
              {documentFiles.map((file, index) => (
                <SortableItem
                  key={file.id}
                  file={file}
                  index={index}
                  onRemove={removeDocumentFile}
                />
              ))}
              {documentFiles.length === 0 && (
                <span style={{ color: "#aaa", alignSelf: "center" }}>
                  Перетащи сюда документы
                </span>
              )}
            </DroppableContainer>
          </SortableContext>
        </div>
      </div>

      {/* ── Превью перетаскиваемого элемента ── */}
      <DragOverlay>
        {activeFile && (
                  <img
                      alt=""
            src={activeFile.preview}
            width={100}
            height={100}
            style={{
              objectFit: "cover",
              borderRadius: "6px",
              opacity: 0.85,
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            }}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}

